import {useCallback, useMemo, useState, useEffect, useRef} from 'react'
import {
  createLocalVideoTrack,
  createLocalAudioTrack,
  LocalVideoTrack,
  LocalAudioTrack,
} from 'livekit-client'
import {useLocalStorage} from '@/shared/lib'
import {STORAGE_KEYS} from '@/shared/config'

const AUDIO_TRACK_CONSTRAINTS: MediaTrackConstraints = {
  autoGainControl: true,
  noiseSuppression: true,
  echoCancellation: true,
}

function detectFrontCamera(track: MediaStreamTrack): boolean {
  const facingMode = track.getSettings().facingMode
  if (facingMode === 'environment') return false
  if (facingMode === 'user') return true
  // Desktop webcams / browsers that don't report facingMode — default to front
  return true
}

export interface UseMediaPreviewResult {
  isLoading: boolean
  isVideoLoading: boolean
  error: string | null
  hasAudio: boolean
  hasVideo: boolean
  isAudioEnabled: boolean
  isVideoEnabled: boolean
  isFrontCamera: boolean
  toggleAudio: () => void
  toggleVideo: () => void
  videoRefCallback: (element: HTMLVideoElement | null) => void
  stopAllTracks: () => void
  deviceWarning: string | null
  /** Raw mic MediaStreamTrack for audio level monitoring in pre-join lobby */
  audioMediaStreamTrack: MediaStreamTrack | null
  switchAudioDevice: (deviceId: string) => Promise<void>
  switchVideoDevice: (deviceId: string) => Promise<void>
}

export function useMediaPreview(): UseMediaPreviewResult {
  // Persisted device states (read from localStorage before other state)
  const [isAudioEnabled, setIsAudioEnabled] = useLocalStorage<boolean>(
    STORAGE_KEYS.AUDIO_ENABLED,
    true,
  )
  const [isVideoEnabled, setIsVideoEnabled] = useLocalStorage<boolean>(
    STORAGE_KEYS.VIDEO_ENABLED,
    true,
  )

  // Media state — skip loading if all devices are disabled
  const [isLoading, setIsLoading] = useState(isAudioEnabled || isVideoEnabled)
  const [isVideoLoading, setIsVideoLoading] = useState(isVideoEnabled)
  const [hasAudio, setHasAudio] = useState(!isAudioEnabled)
  const [hasVideo, setHasVideo] = useState(!isVideoEnabled)
  const [error, setError] = useState<string | null>(null)

  const [isFrontCamera, setIsFrontCamera] = useState(true)

  // Refs to access current enabled state inside async init effect
  const audioEnabledRef = useRef(isAudioEnabled)
  audioEnabledRef.current = isAudioEnabled
  const videoEnabledRef = useRef(isVideoEnabled)
  videoEnabledRef.current = isVideoEnabled

  // Tracks
  const [videoTrack, setVideoTrack] = useState<LocalVideoTrack | null>(null)
  const videoTrackRef = useRef<LocalVideoTrack | null>(null)
  const audioTrackRef = useRef<LocalAudioTrack | null>(null)
  const [audioMediaStreamTrack, setAudioMediaStreamTrack] = useState<MediaStreamTrack | null>(null)

  // Track video element with state to trigger re-render when element is available
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null)
  const videoRefCallback = useCallback((element: HTMLVideoElement | null) => {
    setVideoElement(element)
  }, [])

  // Initialize media devices (only for enabled devices)
  useEffect(() => {
    let isMounted = true

    const initMedia = async () => {
      const shouldInitVideo = videoEnabledRef.current
      const shouldInitAudio = audioEnabledRef.current

      // If both devices are disabled, skip initialization entirely
      if (!shouldInitVideo && !shouldInitAudio) {
        // Assume devices are available (user explicitly disabled them before)
        if (isMounted) {
          setHasVideo(true)
          setHasAudio(true)
          setIsLoading(false)
        }
        return
      }

      setIsLoading(true)

      try {
        // Only create video track if video is enabled
        if (shouldInitVideo) {
          try {
            const savedVideoDeviceId = localStorage.getItem(STORAGE_KEYS.VIDEO_DEVICE_ID)
            const track = await createLocalVideoTrack({
              resolution: {width: 640, height: 480},
              deviceId: savedVideoDeviceId ? {ideal: savedVideoDeviceId} : undefined,
            })
            if (isMounted) {
              videoTrackRef.current = track
              setVideoTrack(track)
              setHasVideo(true)
              setIsFrontCamera(detectFrontCamera(track.mediaStreamTrack))
              if (!savedVideoDeviceId) {
                const actualId = track.mediaStreamTrack.getSettings().deviceId
                if (actualId && actualId !== 'default') {
                  localStorage.setItem(STORAGE_KEYS.VIDEO_DEVICE_ID, actualId)
                }
              }
            } else {
              track.stop()
            }
          } catch (e) {
            console.warn('[useMediaPreview] No camera available:', e)
            if (isMounted) setHasVideo(false)
          } finally {
            if (isMounted) setIsVideoLoading(false)
          }
        } else {
          // Assume camera is available (user explicitly disabled it before)
          if (isMounted) setHasVideo(true)
        }

        // Only create audio track if audio is enabled
        if (shouldInitAudio) {
          try {
            const savedAudioDeviceId = localStorage.getItem(STORAGE_KEYS.AUDIO_DEVICE_ID)
            const audioTrack = await createLocalAudioTrack({
              ...AUDIO_TRACK_CONSTRAINTS,
              deviceId: savedAudioDeviceId ? {ideal: savedAudioDeviceId} : undefined,
            })
            if (isMounted) {
              audioTrackRef.current = audioTrack
              setAudioMediaStreamTrack(audioTrack.mediaStreamTrack)
              setHasAudio(true)
              // Persist the actual device ID used so both lobby and room agree
              if (!savedAudioDeviceId) {
                const actualId = audioTrack.mediaStreamTrack.getSettings().deviceId
                if (actualId && actualId !== 'default') {
                  localStorage.setItem(STORAGE_KEYS.AUDIO_DEVICE_ID, actualId)
                }
              }
            } else {
              audioTrack.stop()
            }
          } catch (e) {
            console.warn('[useMediaPreview] No microphone available:', e)
            if (isMounted) setHasAudio(false)
          }
        } else {
          // Assume microphone is available (user explicitly disabled it before)
          if (isMounted) setHasAudio(true)
        }
      } catch (e) {
        console.error('[useMediaPreview] Media initialization error:', e)
        if (isMounted) {
          setError(
            e instanceof Error ? e.message : 'Не удалось получить доступ к камере и микрофону',
          )
        }
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    initMedia()

    return () => {
      isMounted = false
    }
  }, [])

  // Cleanup tracks on unmount
  useEffect(() => {
    return () => {
      videoTrackRef.current?.stop()
      audioTrackRef.current?.stop()
    }
  }, [])

  // Attach/detach video track when track, video element, or enabled state changes
  useEffect(() => {
    if (!videoTrack || !videoElement) {
      return
    }

    if (isVideoEnabled) {
      // Attach track to video element
      videoTrack.attach(videoElement)
      console.log('[useMediaPreview] Video track attached')

      return () => {
        videoTrack.detach(videoElement)
        console.log('[useMediaPreview] Video track detached')
      }
    }
  }, [videoTrack, videoElement, isVideoEnabled])

  const toggleAudio = useCallback(async () => {
    const newState = !isAudioEnabled
    setIsAudioEnabled(newState)

    if (newState) {
      if (audioTrackRef.current) {
        // Await unmute so that if LiveKit internally restarts the track
        // (e.g. readyState === 'ended'), we get the fresh MediaStreamTrack.
        await audioTrackRef.current.unmute()
        setAudioMediaStreamTrack(audioTrackRef.current.mediaStreamTrack)
      } else {
        // Create track on demand (was disabled at init)
        try {
          const audioTrack = await createLocalAudioTrack(AUDIO_TRACK_CONSTRAINTS)
          audioTrackRef.current = audioTrack
          setAudioMediaStreamTrack(audioTrack.mediaStreamTrack)
          setHasAudio(true)
        } catch (e) {
          console.warn('[useMediaPreview] No microphone available:', e)
          setHasAudio(false)
          setIsAudioEnabled(false)
        }
      }
    } else {
      audioTrackRef.current?.mute()
      setAudioMediaStreamTrack(null)
    }
  }, [isAudioEnabled, setIsAudioEnabled])

  const toggleVideo = useCallback(async () => {
    const newState = !isVideoEnabled
    setIsVideoEnabled(newState)

    if (newState) {
      setIsVideoLoading(true)
      try {
        const existing = videoTrackRef.current
        if (existing) {
          await existing.unmute()
        } else {
          const track = await createLocalVideoTrack({
            resolution: {width: 640, height: 480},
          })
          videoTrackRef.current = track
          setVideoTrack(track)
          setHasVideo(true)
          setIsFrontCamera(detectFrontCamera(track.mediaStreamTrack))
        }
      } catch (e) {
        console.warn('[useMediaPreview] No camera available:', e)
        setHasVideo(false)
        setIsVideoEnabled(false)
      } finally {
        setIsVideoLoading(false)
      }
    } else {
      videoTrackRef.current?.mute()
    }
  }, [isVideoEnabled, setIsVideoEnabled])

  const stopAllTracks = useCallback(() => {
    videoTrackRef.current?.stop()
    videoTrackRef.current = null
    audioTrackRef.current?.stop()
    audioTrackRef.current = null
  }, [])

  const switchAudioDevice = useCallback(async (deviceId: string) => {
    const track = audioTrackRef.current
    if (!track) return
    await track.setDeviceId(deviceId)
    if (audioEnabledRef.current) {
      setAudioMediaStreamTrack(track.mediaStreamTrack)
    }
  }, [])

  const switchVideoDevice = useCallback(async (deviceId: string) => {
    const track = videoTrackRef.current
    if (!track) return
    await track.setDeviceId(deviceId)
    setIsFrontCamera(detectFrontCamera(track.mediaStreamTrack))
  }, [])

  const deviceWarning = useMemo(() => {
    if (isLoading) return null
    if (!hasAudio && !hasVideo) {
      return 'Камера и микрофон не найдены. Вы можете войти, но участники не увидят и не услышат вас.'
    }
    if (!hasAudio) return 'Микрофон не найден. Вы можете войти, но участники не услышат вас.'
    if (!hasVideo) return 'Камера не найдена. Вы можете войти только с аудио.'
    return null
  }, [isLoading, hasAudio, hasVideo])

  return {
    isLoading,
    isVideoLoading,
    error,
    hasAudio,
    hasVideo,
    isAudioEnabled,
    isVideoEnabled,
    isFrontCamera,
    toggleAudio,
    toggleVideo,
    videoRefCallback,
    stopAllTracks,
    deviceWarning,
    audioMediaStreamTrack,
    switchAudioDevice,
    switchVideoDevice,
  }
}
